import React, { useMemo, useState } from 'react';
import { X, Check, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface AITextCompareItem {
  platform: string;
  oldContent: string;
  newContent: string;
}

interface AITextCompareModalProps {
  isOpen: boolean;
  items: AITextCompareItem[];
  onClose: () => void;
  onApply: (selection: Record<string, 'old' | 'new'>) => void;
}

export const AITextCompareModal: React.FC<AITextCompareModalProps> = ({
  isOpen,
  items,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();

  const initialSelection = useMemo(() => {
    const map: Record<string, 'old' | 'new'> = {};
    for (const it of items) map[it.platform] = 'new';
    return map;
  }, [items]);

  const [selection, setSelection] = useState<Record<string, 'old' | 'new'>>(initialSelection);

  React.useEffect(() => {
    setSelection(initialSelection);
  }, [initialSelection]);

  if (!isOpen) return null;

  const setAll = (value: 'old' | 'new') => {
    const map: Record<string, 'old' | 'new'> = {};
    for (const it of items) map[it.platform] = value;
    setSelection(map);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Panel */}
        <div className="relative bg-white rounded-xl border border-black shadow-xl w-full max-w-4xl p-6 sm:my-8 sm:p-8 text-left">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-primary-600">
              <ArrowLeftRight className="h-6 w-6" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('contentCreation.modals.compareText.title', 'Compare and choose your text')}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">{t('common.close', 'Close')}</span>
            </button>
          </div>

          {/* Bulk controls */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <button
              type="button"
              onClick={() => setAll('old')}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              {t('contentCreation.modals.compareText.keepOldAll', 'Keep all old')}
            </button>
            <button
              type="button"
              onClick={() => setAll('new')}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-primary-600 rounded-md text-sm text-primary-600 bg-white hover:bg-primary-50"
            >
              {t('contentCreation.modals.compareText.useNewAll', 'Use all new')}
            </button>
          </div>

          {/* Items */}
          <div className="space-y-6">
            {items.map((it) => (
              <div key={it.platform} className="border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                  <div className="text-sm font-medium text-gray-700">
                    {it.platform.toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name={`sel-${it.platform}`}
                        className="text-primary-600 focus:ring-primary-500"
                        checked={selection[it.platform] === 'old'}
                        onChange={() => setSelection((s) => ({ ...s, [it.platform]: 'old' }))}
                      />
                      <span className="text-gray-600">{t('contentCreation.modals.compareText.keepOld', 'Keep old')}</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name={`sel-${it.platform}`}
                        className="text-primary-600 focus:ring-primary-500"
                        checked={selection[it.platform] === 'new'}
                        onChange={() => setSelection((s) => ({ ...s, [it.platform]: 'new' }))}
                      />
                      <span className="text-primary-700">{t('contentCreation.modals.compareText.useNew', 'Use new')}</span>
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  <div className="p-4 border-r md:border-r border-gray-200">
                    <div className="text-xs font-semibold text-gray-500 mb-2">
                      {t('contentCreation.modals.compareText.oldLabel', 'Current')}
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-64 overflow-auto">
                      {it.oldContent}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-xs font-semibold text-gray-500 mb-2">
                      {t('contentCreation.modals.compareText.newLabel', 'Generated')}
                    </div>
                    <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-64 overflow-auto">
                      {it.newContent}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-3 gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={() => onApply(selection)}
              className="w-full sm:w-auto inline-flex justify-center items-center rounded-lg border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('contentCreation.modals.compareText.apply', 'Apply selection')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


